import React, { useState } from 'react';
import { User } from '../types/User';
import { UserCircle, Edit3, Save, X, Camera } from 'lucide-react';
import { AuthService } from '../services/authService';

interface MyProfileProps {
  currentUser: User;
  onUpdateUser: (updatedUser: User) => void;
}

const MyProfile: React.FC<MyProfileProps> = ({ currentUser, onUpdateUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: currentUser.name,
    company: currentUser.company,
    position: currentUser.position,
    country: currentUser.country,
    about: currentUser.about || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB' }));
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, image: 'Please select a valid image file' }));
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous errors
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const saveProfile = async () => {
      let avatarUrl = currentUser.avatar;
      
      // Convert and save image if selected
      if (selectedImage) {
        try {
          avatarUrl = await AuthService.convertImageToBase64(selectedImage);
        } catch (error) {
          setErrors({ image: 'Failed to process image' });
          return;
        }
      }

      // Update profile in Supabase
      const { user, error } = await AuthService.updateProfile(currentUser.id, {
        name: formData.name,
        company: formData.company,
        position: formData.position,
        country: formData.country,
        about: formData.about || undefined,
        avatar_url: avatarUrl
      });

      if (error) {
        setErrors({ name: error });
      } else if (user) {
        onUpdateUser(user);
        setIsEditing(false);
        setSelectedImage(null);
        setImagePreview(null);
      }
    };

    saveProfile();
  };

  const handleCancel = () => {
    setFormData({
      name: currentUser.name,
      company: currentUser.company,
      position: currentUser.position,
      country: currentUser.country,
      about: currentUser.about || ''
    });
    setErrors({});
    setIsEditing(false);
    setSelectedImage(null);
    setImagePreview(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
          <p className="text-gray-600">Manage your profile information</p>
        </div>
        
        <div className="flex justify-center lg:justify-end">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors w-full lg:w-auto"
              style={{ backgroundColor: '#C89116' }}
            >
              <Edit3 className="w-4 h-4" />
              <span>Edit profile</span>
            </button>
          ) : (
            <div className="flex flex-col lg:flex-row space-y-3 lg:space-y-0 lg:space-x-3 w-full lg:w-auto">
              <button
                onClick={handleCancel}
                className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors w-full lg:w-auto"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                className="flex items-center justify-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors w-full lg:w-auto"
                style={{ backgroundColor: '#C89116' }}
              >
                <Save className="w-4 h-4" />
                <span>Save changes</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Content */}
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Profile Picture Section */}
        <div className="p-6 border-b">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
            <div className="relative">
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center">
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt="Profile preview" 
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : currentUser.avatar ? (
                  <img 
                    src={currentUser.avatar} 
                    alt="Profile" 
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                <span className="text-2xl font-semibold text-gray-600">
                  {currentUser.name.split(' ').map(n => n[0]).join('')}
                </span>
                )}
              </div>
              {isEditing && (
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow-lg border flex items-center justify-center hover:bg-gray-50 cursor-pointer">
                  <Camera className="w-4 h-4 text-gray-600" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-gray-800">{currentUser.name}</h2>
              <p className="text-gray-600">{currentUser.position}</p>
              <p className="text-gray-500">{currentUser.company}</p>
              {selectedImage && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ New image selected: {selectedImage.name}
                </p>
              )}
              {errors.image && (
                <p className="text-sm text-red-600 mt-2">{errors.image}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="p-6 space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            {isEditing ? (
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800">
                {currentUser.name}
              </div>
            )}
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            {isEditing ? (
              <div>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                    errors.company ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your company name"
                />
                {errors.company && <p className="mt-1 text-sm text-red-600">{errors.company}</p>}
              </div>
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800">
                {currentUser.company}
              </div>
            )}
          </div>

          {/* Job Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Position
            </label>
            {isEditing ? (
              <div>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                    errors.position ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your job position"
                />
                {errors.position && <p className="mt-1 text-sm text-red-600">{errors.position}</p>}
              </div>
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800">
                {currentUser.position}
              </div>
            )}
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            {isEditing ? (
              <div>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                    errors.country ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your country"
                />
                {errors.country && <p className="mt-1 text-sm text-red-600">{errors.country}</p>}
              </div>
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800">
                {currentUser.country}
              </div>
            )}
          </div>

          {/* About */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              About
            </label>
            {isEditing ? (
              <textarea
                name="about"
                value={formData.about}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600"
                placeholder="Tell us about yourself and your professional background..."
              />
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 min-h-[100px]">
                {currentUser.about || 'No description provided'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;